import {
  CurrentBroadcast,
  BroadcastStreamShape,
  InstancePerson,
  UnpersistedCurrentBroadcast,
} from 'broadcasts/types';
import { IconName } from '@ardoq/icons';
import { t, type I18nLocale } from '@ardoq/i18n';
import { featureOperations, Features } from '@ardoq/features';
import { getFeatureContext } from 'streams/featureContext$';
import { broadcastContentTypeIconMapping } from 'broadcasts/config';
import { LabeledValue } from 'aqTypes';
import {
  APIBroadcastAttributes,
  AdvancedSearchFilterCondition,
  AdvancedSearchFilterConditionByReference,
  ArdoqId,
  AudiencePreview,
  AudiencePreviewRow,
  LightAudiencePreviewRow,
  AudienceStartingPage,
  BroadcastAudience,
  BroadcastAudienceType,
  BroadcastComponent,
  BroadcastContent,
  BroadcastContentType,
  BroadcastFilterCondition,
  BroadcastFilterConditionType,
  BroadcastMessage,
  BroadcastMessageContent,
  BroadcastPerson,
  BroadcastReportContent,
  BroadcastSchedule,
  BroadcastScope,
  BroadcastStatus,
  BroadcastSurveyContent,
  EmailAudience,
  GremlinAudience,
  IntervalType,
  PredefinedAudience,
  PredefinedQueryOption,
  RepeatingSchedule,
  SurveyCompleteFilterCondition,
  SurveyIncompleteFilterCondition,
  SurveyNotUpdatedSinceFilterCondition,
  WorkspaceAudience,
  APIAudiencePreviewResponse,
  BroadcastInstance,
} from '@ardoq/api-types';
import { pluralize, toByIdDictionary } from '@ardoq/common-helpers';
import { getSolutionMetadata } from '@ardoq/core';
import { isEqual, omit, uniqBy } from 'lodash';
import {
  SUPPORTED_DEFAULT_LANGUAGES,
  getDefaultBroadcastMessage,
} from './consts';
import { DEFAULT_LANGUAGE } from 'supportedLanguages';
import { addDays, currentDate, format, parseDate } from '@ardoq/date-time';
import { type Locale } from '@ardoq/locale';
import { dispatchAction } from '@ardoq/rxbeach';
import { initiateNavigationToEditBroadcastForm } from 'router/navigationActions';
import { showBroadcastDetailsDialog } from './broadcastOverview/actions';
import {
  trackClickedToEditBroadcast,
  BroadcastEditOrigin,
  trackClickedToOpenBroadcastDetailsDialog,
} from './tracking';

export const createStartDate = () => {
  const date = currentDate();
  date.setMinutes(0);
  return date.toISOString();
};

export const getSenderText = (name: string, i18nLocale: I18nLocale): string =>
  name
    ? t(
        i18nLocale,
        'broadcasts:builder.compose.sender.onBehalfOf',
        { name },
        'Ardoq (on behalf of {{name}})'
      )
    : 'Ardoq';

export const createNewBroadcast = (
  senderText: string,
  folderId?: ArdoqId | null,
  i18nLanguage: string = DEFAULT_LANGUAGE
): UnpersistedCurrentBroadcast => {
  const defaultSurvey = getDefaultBroadcastMessage(
    i18nLanguage,
    BroadcastContentType.SURVEY
  );
  return {
    name: '',
    content: {
      contentType: BroadcastContentType.SURVEY,
      filterConditions: [],
      contentId: null,
    },
    audiences: [],
    audienceStartingPage: AudienceStartingPage.SURVEY_OVERVIEW,
    message: {
      subject: defaultSurvey.subject,
      sender: senderText,
      replyToArdoq: false,
      body: defaultSurvey.body,
      companyLogo: null,
      hideLogo: false,
    },
    scheduling: {
      repeating: true,
      startDate: createStartDate(),
      reminderInDays: null,
      interval: createNewInterval(),
      sendOnWeekends: false,
    },
    folderId: folderId ?? null,
  };
};

export const createNewInterval = () => ({
  intervalValue: 1,
  intervalType: IntervalType.WEEK,
});

export const isMessageContent = (
  content: BroadcastContent
): content is BroadcastMessageContent =>
  content.contentType === BroadcastContentType.MESSAGE;

export const isSurveyContent = (
  content: BroadcastContent
): content is BroadcastSurveyContent =>
  content.contentType === BroadcastContentType.SURVEY;

export const isReportContent = (
  content: BroadcastContent
): content is BroadcastReportContent =>
  content.contentType === BroadcastContentType.REPORT;

const hasRedirectLegacyDiscoverFeature = (): boolean =>
  featureOperations.hasFeature(
    getFeatureContext(),
    Features.REDIRECT_LEGACY_DISCOVER
  );

export const getDefaultAudienceStartingPage = (
  contentType: BroadcastContentType
): AudienceStartingPage => {
  switch (contentType) {
    case BroadcastContentType.MESSAGE:
      return AudienceStartingPage.PLAIN;
    case BroadcastContentType.SURVEY:
      return AudienceStartingPage.SURVEY_OVERVIEW;
    case BroadcastContentType.REPORT:
      return hasRedirectLegacyDiscoverFeature()
        ? AudienceStartingPage.REPORT_ARDOQ
        : AudienceStartingPage.REPORT_DISCOVER;
  }
};

const isValidMessageContent = (content: BroadcastContent) =>
  isMessageContent(content) && content.workspaceId && content.componentType;

const isValidSurveyContent = (content: BroadcastContent) =>
  isSurveyContent(content) && content.contentId;

const isValidReportContent = (content: BroadcastContent) =>
  isReportContent(content) &&
  content.contentId &&
  content.columnName &&
  content.aggregate &&
  content.predicate &&
  content.threshold !== null;

export const isValidContent = (content: BroadcastContent) =>
  isValidMessageContent(content) ||
  isValidSurveyContent(content) ||
  isValidReportContent(content);

export const isValidAudience = (
  content: BroadcastContent,
  audiences: BroadcastAudience[]
) => Boolean(isValidContent(content) && audiences.length);

export const getQueryRepresentation = ({ label }: PredefinedQueryOption) => {
  return label;
};

const ENGLISH_AUDIENCE_TYPE_LABEL: Record<BroadcastAudienceType, string> = {
  [BroadcastAudienceType.PREDEFINED]: 'Predefined query',
  [BroadcastAudienceType.GREMLIN]: 'Gremlin query',
  [BroadcastAudienceType.EMAIL]: 'Email',
  [BroadcastAudienceType.WORKSPACE]: 'People workspace',
};

const AUDIENCE_TYPE_KEY: Record<BroadcastAudienceType, string> = {
  [BroadcastAudienceType.PREDEFINED]:
    'broadcasts:audience.type.predefinedQuery',
  [BroadcastAudienceType.GREMLIN]: 'broadcasts:audience.type.gremlinQuery',
  [BroadcastAudienceType.EMAIL]: 'broadcasts:audience.type.email',
  [BroadcastAudienceType.WORKSPACE]: 'broadcasts:audience.type.peopleWorkspace',
};

export const getAudienceTypeRepresentation = (
  audienceType: BroadcastAudienceType,
  i18nLocale: I18nLocale
): string =>
  t(
    i18nLocale,
    AUDIENCE_TYPE_KEY[audienceType],
    ENGLISH_AUDIENCE_TYPE_LABEL[audienceType]
  );

const ENGLISH_AUDIENCE_STARTING_PAGE_LABEL: Record<
  AudienceStartingPage,
  string
> = {
  [AudienceStartingPage.SURVEY_OVERVIEW]: 'Component overview page',
  [AudienceStartingPage.MY_TASKS]: 'Tasks page',
  [AudienceStartingPage.DISCOVER_SURVEY]: 'Separate links in discover',
  [AudienceStartingPage.SURVEY_COMPONENT]: 'Separate component links',
  [AudienceStartingPage.PLAIN]: 'Separate component links',
  [AudienceStartingPage.DISCOVER_COMPONENT]: 'Separate component links',
  [AudienceStartingPage.REPORT_ARDOQ]: 'Separate component links',
  [AudienceStartingPage.REPORT_DISCOVER]: 'Separate component links',
};

const AUDIENCE_STARTING_PAGE_KEY: Record<AudienceStartingPage, string> = {
  [AudienceStartingPage.SURVEY_OVERVIEW]:
    'broadcasts:audience.startingPage.componentOverview',
  [AudienceStartingPage.MY_TASKS]: 'broadcasts:audience.startingPage.tasks',
  [AudienceStartingPage.DISCOVER_SURVEY]:
    'broadcasts:audience.startingPage.discoverLinks',
  [AudienceStartingPage.SURVEY_COMPONENT]:
    'broadcasts:audience.startingPage.componentLinks',
  [AudienceStartingPage.PLAIN]:
    'broadcasts:audience.startingPage.componentLinks',
  [AudienceStartingPage.DISCOVER_COMPONENT]:
    'broadcasts:audience.startingPage.componentLinks',
  [AudienceStartingPage.REPORT_ARDOQ]:
    'broadcasts:audience.startingPage.componentLinks',
  [AudienceStartingPage.REPORT_DISCOVER]:
    'broadcasts:audience.startingPage.componentLinks',
};

export const getAudienceStartingPageRepresentation = (
  audienceStartingPage: AudienceStartingPage,
  i18nLocale: I18nLocale
): string =>
  t(
    i18nLocale,
    AUDIENCE_STARTING_PAGE_KEY[audienceStartingPage],
    ENGLISH_AUDIENCE_STARTING_PAGE_LABEL[audienceStartingPage]
  );

const audienceIsOfType =
  <T extends BroadcastAudience>(audienceType: BroadcastAudienceType) =>
  (audience: BroadcastAudience): audience is T =>
    audience.audienceType === audienceType;

export const withoutAudienceOfType =
  (audienceType: BroadcastAudienceType) => (audiences: BroadcastAudience[]) => {
    return audiences.filter(
      audience => !audienceIsOfType(audienceType)(audience)
    );
  };

export const withAddedOrReplacedAudience = (
  audiences: BroadcastAudience[],
  audience: BroadcastAudience
) => {
  const currentIndex = audiences.findIndex(
    audienceIsOfType(audience.audienceType)
  );
  return currentIndex === -1
    ? [...audiences, audience]
    : [
        ...audiences.slice(0, currentIndex),
        audience,
        ...audiences.slice(currentIndex + 1),
      ];
};

const containsAudienceOfType =
  (audienceType: BroadcastAudienceType) => (audiences: BroadcastAudience[]) =>
    audiences.some(audienceIsOfType(audienceType));

export const containsPredefinedAudience = containsAudienceOfType(
  BroadcastAudienceType.PREDEFINED
);

export const containsGremlinAudience = containsAudienceOfType(
  BroadcastAudienceType.GREMLIN
);

export const containsEmailAudience = containsAudienceOfType(
  BroadcastAudienceType.EMAIL
);

export const containsWorkspaceAudience = containsAudienceOfType(
  BroadcastAudienceType.WORKSPACE
);

const isPredefinedAudience = audienceIsOfType<PredefinedAudience>(
  BroadcastAudienceType.PREDEFINED
);

const isGremlinAudience = audienceIsOfType<GremlinAudience>(
  BroadcastAudienceType.GREMLIN
);

const isEmailAudience = audienceIsOfType<EmailAudience>(
  BroadcastAudienceType.EMAIL
);

const isWorkspaceAudience = audienceIsOfType<WorkspaceAudience>(
  BroadcastAudienceType.WORKSPACE
);

export const findPredefinedAudience = (audiences: BroadcastAudience[]) => {
  return audiences.find(isPredefinedAudience) || null;
};

export const findGremlinAudience = (audiences: BroadcastAudience[]) => {
  return audiences.find(isGremlinAudience) || null;
};

export const findEmailAudience = (audiences: BroadcastAudience[]) => {
  return audiences.find(isEmailAudience) || null;
};

export const findWorkspaceAudience = (audiences: BroadcastAudience[]) => {
  return audiences.find(isWorkspaceAudience) || null;
};

export type AudienceWarningKind =
  | { type: 'missingContactEmails'; count: number }
  | { type: 'gremlinResultFormat' }
  | { type: 'gremlinPathEndsNonPerson' };

export const getAudienceWarningMessage = (
  kind: AudienceWarningKind,
  i18nLocale: I18nLocale
): string => {
  switch (kind.type) {
    case 'missingContactEmails': {
      const params = { count: kind.count };
      return kind.count === 1
        ? t(
            i18nLocale,
            'broadcasts:audience.missingContactEmailsSingular',
            params,
            '1 audience is missing a Contact Email field. Only people whose Person component has their email stored in a Contact Email field will be able to receive the broadcast.'
          )
        : t(
            i18nLocale,
            'broadcasts:audience.missingContactEmailsPlural',
            params,
            `${kind.count} audiences are missing a Contact Email field. Only people whose Person component has their email stored in a Contact Email field will be able to receive the broadcast.`
          );
    }
    case 'gremlinResultFormat':
      return t(
        i18nLocale,
        'broadcasts:audience.dialogs.gremlin.warningResultFormat',
        'The results are not in the expected format. To link components to people the query must return a list of paths between them. This may be achieved by ending your query with .path()'
      );
    case 'gremlinPathEndsNonPerson':
      return t(
        i18nLocale,
        'broadcasts:audience.dialogs.gremlin.warningPathEndsNonPerson',
        'The results are not in the expected format. To link components to people each path returned by the query must end at a Person component.'
      );
  }
};

export const getPersonRepresentation = (
  person: BroadcastPerson | InstancePerson
) => {
  return person.name ?? person.email;
};

export const getPersonSecondaryRepresentation = (person: BroadcastPerson) => {
  return person.name ? person.email : null;
};

const getUniquePeople = (people: BroadcastPerson[]) => {
  return uniqBy(people, getPersonRepresentation);
};

export const getUniqueAudienceCount = (audiencePreview: AudiencePreview) => {
  const people = audiencePreview.map(({ person }) => person);
  const uniquePeople = getUniquePeople(people);
  return uniquePeople.length;
};

const isMissingEmail = ({ email }: BroadcastPerson) => !email;

const getPeopleSelection = (
  people: BroadcastPerson[],
  limitedToPersonIds: ArdoqId[]
) => {
  if (!limitedToPersonIds.length) return people;
  return people.filter(({ _id }) => _id && limitedToPersonIds.includes(_id));
};

const countPeopleWithoutEmail = (people: BroadcastPerson[]) => {
  return getUniquePeople(people).filter(isMissingEmail).length;
};

export const getWarningKindFromPeople = (
  people: BroadcastPerson[],
  limitedToPersonIds: ArdoqId[] = []
): AudienceWarningKind | null => {
  const peopleSelection = getPeopleSelection(people, limitedToPersonIds);
  const numberOfMissingEmails = countPeopleWithoutEmail(peopleSelection);
  return numberOfMissingEmails
    ? { type: 'missingContactEmails', count: numberOfMissingEmails }
    : null;
};

export const getWarningKindFromAudiencePreview = (
  audiencePreview: AudiencePreview
): AudienceWarningKind | null => {
  const people = audiencePreview.map(({ person }) => person);
  return getWarningKindFromPeople(people, []);
};

export const doesAudiencePreviewHaveWarnings = (
  audiencePreview: AudiencePreview
) => {
  const people = audiencePreview.map(({ person }) => person);
  return countPeopleWithoutEmail(people) > 0;
};

export const isRepeatingSchedule = (
  schedule: BroadcastSchedule
): schedule is RepeatingSchedule => schedule.repeating;

export const isRuntimeDue = (runtime: string, now: string): boolean =>
  parseDate(runtime).getTime() <= parseDate(now).getTime();

export const getIconForContentType = (
  contentType: BroadcastContentType
): IconName => broadcastContentTypeIconMapping.get(contentType) as IconName;

export const pluralizeOptionLabels = <T>(
  options: LabeledValue<T>[],
  count: number
) => {
  if (count === 1) return options;
  return options.map(option => ({
    ...option,
    label: pluralize(option.label, count),
  }));
};

export function getFilterConditionByType(
  filterConditions: BroadcastFilterCondition[],
  conditionType: BroadcastFilterConditionType.SURVEY_INCOMPLETE
): SurveyIncompleteFilterCondition | null;
export function getFilterConditionByType(
  filterConditions: BroadcastFilterCondition[],
  conditionType: BroadcastFilterConditionType.SURVEY_COMPLETE
): SurveyCompleteFilterCondition | null;
export function getFilterConditionByType(
  filterConditions: BroadcastFilterCondition[],
  conditionType: BroadcastFilterConditionType.SURVEY_NOT_UPDATED_IN
): SurveyNotUpdatedSinceFilterCondition | null;
export function getFilterConditionByType(
  filterConditions: BroadcastFilterCondition[],
  conditionType: BroadcastFilterConditionType.ADVANCED_SEARCH
): AdvancedSearchFilterCondition | null;
export function getFilterConditionByType(
  filterConditions: BroadcastFilterCondition[],
  conditionType: BroadcastFilterConditionType.ADVANCED_SEARCH_BY_REFERENCE
): AdvancedSearchFilterConditionByReference | null;
export function getFilterConditionByType(
  filterConditions: BroadcastFilterCondition[],
  conditionType: BroadcastFilterConditionType
): BroadcastFilterCondition | null;
export function getFilterConditionByType(
  filterConditions: BroadcastFilterCondition[],
  conditionType: BroadcastFilterConditionType
) {
  return (
    filterConditions.find(cond => cond.conditionType === conditionType) ?? null
  );
}

export const isPersistedBroadcast = (
  broadcast: CurrentBroadcast | null
): broadcast is Readonly<APIBroadcastAttributes> => {
  if (broadcast === null) return false;
  return Boolean((broadcast as APIBroadcastAttributes)._id);
};

export const isRunningStatus = (status: BroadcastStatus): boolean =>
  status === BroadcastStatus.RUNNING || status === BroadcastStatus.WARNING;

export const isRunningBroadcast = ({ status }: APIBroadcastAttributes) => {
  return isRunningStatus(status);
};

export const findBroadcast = (
  broadcasts: APIBroadcastAttributes[],
  broadcastId: ArdoqId | null
) => {
  return broadcasts.find(broadcast => broadcast._id === broadcastId) ?? null;
};

export const doesCurrentBroadcastHaveUnsavedProgress = ({
  broadcasts,
  currentBroadcast,
}: Pick<BroadcastStreamShape, 'broadcasts' | 'currentBroadcast'>) => {
  if (!isPersistedBroadcast(currentBroadcast)) return true;
  const persistedCurrentBroadcast = findBroadcast(
    broadcasts,
    currentBroadcast._id
  );
  return !isEqual(currentBroadcast, persistedCurrentBroadcast);
};

export const mayApplyBroadcast = (
  state: Pick<BroadcastStreamShape, 'broadcasts' | 'currentBroadcast'>,
  broadcastId: ArdoqId
): boolean => {
  const isEditingThisBroadcast =
    isPersistedBroadcast(state.currentBroadcast) &&
    state.currentBroadcast._id === broadcastId;
  return (
    !isEditingThisBroadcast || !doesCurrentBroadcastHaveUnsavedProgress(state)
  );
};

export const mayCurrentBroadcastBeOverwritten = (
  {
    broadcasts,
    currentBroadcast,
    userName,
  }: Pick<BroadcastStreamShape, 'broadcasts' | 'currentBroadcast' | 'userName'>,
  i18nLanguage: string,
  i18nLocale: I18nLocale
) => {
  if (currentBroadcast === null) return true;
  if (
    !isPersistedBroadcast(currentBroadcast) &&
    isEqual(
      omit(currentBroadcast, 'scheduling.startDate'),
      omit(
        createNewBroadcast(
          getSenderText(userName, i18nLocale),
          null,
          i18nLanguage
        ),
        'scheduling.startDate'
      )
    )
  ) {
    return true;
  }
  return !doesCurrentBroadcastHaveUnsavedProgress({
    broadcasts,
    currentBroadcast,
  });
};

export const withAddedOrUpdatedBroadcast = (
  broadcasts: APIBroadcastAttributes[],
  broadcast: APIBroadcastAttributes
) => {
  const currentIndex = broadcasts.findIndex(({ _id }) => _id === broadcast._id);
  if (currentIndex === -1) {
    return [...broadcasts, broadcast];
  }
  return [
    ...broadcasts.slice(0, currentIndex),
    broadcast,
    ...broadcasts.slice(currentIndex + 1),
  ];
};

export const getFormattedDate = (date: string, locale: Locale) =>
  format(parseDate(date), 'PP', { locale });

const getDefaultMessageSubject = (
  contentType: BroadcastContentType,
  i18nLanguage: string = DEFAULT_LANGUAGE
): string => getDefaultBroadcastMessage(i18nLanguage, contentType).subject;

const getDefaultMessageBody = (
  contentType: BroadcastContentType,
  i18nLanguage: string = DEFAULT_LANGUAGE
): string => getDefaultBroadcastMessage(i18nLanguage, contentType).body;

const isDefaultMessageSubjectInAnyLocale = (
  contentType: BroadcastContentType,
  subject: string
): boolean =>
  SUPPORTED_DEFAULT_LANGUAGES.some(
    lang => getDefaultMessageSubject(contentType, lang) === subject
  );

const isDefaultMessageBodyInAnyLocale = (
  contentType: BroadcastContentType,
  body: string
): boolean =>
  SUPPORTED_DEFAULT_LANGUAGES.some(
    lang => getDefaultMessageBody(contentType, lang) === body
  );

const getNextMessageSubjectOnContentTypeChange = (
  currentContentType: BroadcastContentType,
  nextContentType: BroadcastContentType,
  currentSubject: string,
  i18nLanguage: string
) => {
  if (isDefaultMessageSubjectInAnyLocale(currentContentType, currentSubject)) {
    return getDefaultMessageSubject(nextContentType, i18nLanguage);
  }
  return currentSubject;
};

const getNextMessageBodyOnContentTypeChange = (
  currentContentType: BroadcastContentType,
  nextContentType: BroadcastContentType,
  currentBody: string,
  i18nLanguage: string
) => {
  if (isDefaultMessageBodyInAnyLocale(currentContentType, currentBody)) {
    return getDefaultMessageBody(nextContentType, i18nLanguage);
  }
  return currentBody;
};

export const getNextMessageOnContentTypeChange = (
  currentContentType: BroadcastContentType,
  nextContentType: BroadcastContentType,
  currentMessage: BroadcastMessage,
  i18nLanguage: string = DEFAULT_LANGUAGE
) => {
  return {
    subject: getNextMessageSubjectOnContentTypeChange(
      currentContentType,
      nextContentType,
      currentMessage.subject,
      i18nLanguage
    ),
    sender: currentMessage.sender,
    replyToArdoq: currentMessage.replyToArdoq,
    body: getNextMessageBodyOnContentTypeChange(
      currentContentType,
      nextContentType,
      currentMessage.body,
      i18nLanguage
    ),
    companyLogo: currentMessage.companyLogo,
  };
};

const getMostTargetedScope = (...scopes: BroadcastScope[]) => {
  if (scopes.includes(BroadcastScope.SOME)) return BroadcastScope.SOME;
  return BroadcastScope.ALL;
};

export const isTargetedSource = (source: BroadcastAudienceType) => {
  return [
    BroadcastAudienceType.GREMLIN,
    BroadcastAudienceType.PREDEFINED,
  ].includes(source);
};

export const isGeneralSource = (source: BroadcastAudienceType) => {
  return [
    BroadcastAudienceType.EMAIL,
    BroadcastAudienceType.WORKSPACE,
  ].includes(source);
};

const withoutGeneralSourcesIfTargetedSourceExists = (
  sources: BroadcastAudienceType[]
) => {
  if (sources.some(isTargetedSource)) return sources.filter(isTargetedSource);
  return sources;
};

export const rowHasSamePersonAs =
  ({ person: person1 }: AudiencePreviewRow) =>
  ({ person: person2 }: AudiencePreviewRow) => {
    if (!person1.email && !person2.email) {
      return person1.name === person2.name;
    }
    return person1.email === person2.email;
  };

export const mergeAudiencePreviewRowsByPerson = (
  audiencePreview: AudiencePreview
) => {
  return audiencePreview.reduce<AudiencePreview>(
    (mergedAudiencePreview, audiencePreviewRow) => {
      const existingRowForPerson = mergedAudiencePreview.find(
        rowHasSamePersonAs(audiencePreviewRow)
      );
      if (!existingRowForPerson) {
        return [...mergedAudiencePreview, audiencePreviewRow];
      }
      const updatedComponentsForPerson = uniqBy(
        [...existingRowForPerson.components, ...audiencePreviewRow.components],
        ({ _id }) => _id
      );
      const updatedScopeForPerson = getMostTargetedScope(
        existingRowForPerson.scope,
        audiencePreviewRow.scope
      );
      const updatedSourcesForPerson =
        withoutGeneralSourcesIfTargetedSourceExists([
          ...existingRowForPerson.sources,
          ...audiencePreviewRow.sources,
        ]);
      const updatedRowForPerson = {
        ...existingRowForPerson,
        components: updatedComponentsForPerson,
        scope: updatedScopeForPerson,
        sources: updatedSourcesForPerson,
      };
      return [
        ...mergedAudiencePreview.filter(row => row !== existingRowForPerson),
        updatedRowForPerson,
      ];
    },
    []
  );
};

export const hasSourceOfType =
  (audienceType: BroadcastAudienceType) =>
  (audiencePreviewRow: AudiencePreviewRow) =>
    audiencePreviewRow.sources.some(source => source === audienceType);

export const getFutureDateISO = (dateString: string, interval: number) => {
  return addDays(parseDate(dateString), interval).toISOString();
};

export const getFutureDate = (
  dateString: string,
  interval: number,
  locale: Locale
) => {
  const futureDateISO = getFutureDateISO(dateString, interval);
  return getFormattedDate(futureDateISO, locale);
};

export const openBroadcast = (broadcast: APIBroadcastAttributes) => {
  if (broadcast.status === BroadcastStatus.UNPUBLISHED) {
    dispatchAction(initiateNavigationToEditBroadcastForm(broadcast._id));
    trackClickedToEditBroadcast(
      BroadcastEditOrigin.BROADCAST_OVERVIEW_TABLE_NAME_CELL,
      getSolutionMetadata(broadcast)
    );
  } else {
    dispatchAction(showBroadcastDetailsDialog(broadcast._id));
    trackClickedToOpenBroadcastDetailsDialog();
  }
};

export const getDefaultFilterConditionState = () => ({ isExpanded: false });

export const toComponentMappedPreviewAudienceRow = (
  previewAudienceRow: LightAudiencePreviewRow,
  previewAudienceComponents: Record<ArdoqId, BroadcastComponent>
): AudiencePreviewRow => {
  const updatedComponents =
    previewAudienceRow.scope === BroadcastScope.ALL
      ? Object.values(previewAudienceComponents)
      : previewAudienceRow.componentIds.map(
          componentId => previewAudienceComponents[componentId]
        );
  return {
    ...previewAudienceRow,
    components: updatedComponents,
  };
};

export const toComponentMappedPreviewAudience = (
  apiAudiencePreview: APIAudiencePreviewResponse
): AudiencePreview => {
  const mappedComponents = toByIdDictionary(apiAudiencePreview.components);
  return apiAudiencePreview.audience.map(audiencePreviewRow =>
    toComponentMappedPreviewAudienceRow(audiencePreviewRow, mappedComponents)
  );
};

/**
 * If an audience entry has scope `ALL`, the components array will be empty, and must be fetched from the instance
 */
export const broadcastInstanceAudienceWithAllScopedComponentsFilled = (
  instance: BroadcastInstance
): BroadcastInstance => {
  return {
    ...instance,
    audience: instance.audience.map(audiencePreviewRow => ({
      ...audiencePreviewRow,
      components:
        audiencePreviewRow.scope === BroadcastScope.ALL
          ? (instance.components ?? [])
          : audiencePreviewRow.components,
    })),
  };
};
